<script lang="ts">
  /**
   * One recurring topic, as the same card a week's theme gets.
   *
   * IT WAS A TABLE ROW, AND THAT WAS THE PROBLEM. Six numeric columns — trend, weeks,
   * questions, peak, span — put the topic's NAME in a truncated cell competing with
   * five figures, and made the two tabs read as two different products: cards on one,
   * a spreadsheet on the other. Same object, same question ("what do they keep asking
   * about?"), so the same card.
   *
   * WHAT A RECURRING TOPIC IS TRIAGED ON is not what a week's theme is triaged on. A
   * theme has a coverage verdict and an action; a topic's whole point is the RUN — how
   * many weeks, how big at its peak, and whether it is still live. So the rail encodes
   * the run rather than coverage: past three weeks it stops being noise and becomes a
   * backlog item, which is the one judgement this view exists to make.
   *
   * The numbers that were columns are a sentence now. Five right-aligned figures per
   * row is a table's answer to "which is biggest", and nobody sorts this list by peak.
   */
  import Sparkline from '../charts/Sparkline.svelte';
  import type { Snippet } from 'svelte';
  import type { TopicRollupRow } from '@api';
  import { weekSpan } from './weeks';

  let {
    topic,
    open = false,
    ontoggle = null,
    children = undefined,
  }: {
    topic: TopicRollupRow;
    open?: boolean;
    ontoggle?: (() => void) | null;
    /** The expanded panel. Passed in rather than imported so this card stays a shape
     *  and the page owns what opening one costs — `<TopicQuestions>` fetches. */
    children?: Snippet;
  } = $props();

  /** A PAINTED BAR, not a left border — `.hair` sets `border-color` on all four sides
   *  and beats a `border-l-*` utility, which silently renders the rail grey. Same trap,
   *  and same fix, as `<DigestThemeCard>`. */
  const rail = $derived(
    topic.status === 'dormant'
      ? 'bg-base-300'
      : topic.weeks_seen >= 3
        ? 'bg-warning'
        : 'bg-base-300',
  );

  const spark = $derived(topic.trend?.map((p) => p.count) ?? []);
  const sparkLabels = $derived(topic.trend?.map((p) => p.week.slice(5)) ?? []);

  /** Shared, and it matters: the copy that used to live here parsed the week in the
   *  VIEWER's zone, so every span west of UTC rendered a day early — "Jul 26 → Aug 9"
   *  under a picker reading "Aug 10". */
  const span = $derived(weekSpan(topic.first_week, topic.last_week));
</script>

<article class="rounded-box relative overflow-hidden border hair bg-base-100">
  <span class="absolute inset-y-0 left-0 w-[3px] {rail}" aria-hidden="true"></span>
  <button
    class="flex w-full items-center gap-4 py-2.5 pr-4 pl-5 text-left hover:bg-base-200/40"
    onclick={() => ontoggle?.()}
    aria-expanded={open}
  >
    <div class="min-w-0 flex-1">
      <div class="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
        <h3 class="redact font-display text-[0.94rem] leading-tight font-semibold">{topic.title}</h3>
        <!-- Same badge, same threshold, same words as the week card. Two views of one
             object should not disagree about when a run starts mattering. -->
        <span
          class="text-[0.68rem] {topic.weeks_seen >= 3
            ? 'font-medium text-primary'
            : 'text-base-content/45'}">{topic.weeks_seen} wks running</span
        >
        {#if topic.status === 'dormant'}
          <span class="text-[0.68rem] text-base-content/40">quiet lately</span>
        {/if}
      </div>

      <div class="mt-1 flex min-w-0 flex-wrap items-center gap-x-2.5 gap-y-1">
        <span class="shrink-0 text-[0.72rem] tabular-nums text-base-content/50">
          {topic.questions} question{topic.questions === 1 ? '' : 's'} · peak {topic.peak_members}
          in a week
        </span>
        {#if span}
          <span class="eyebrow shrink-0 text-[0.6rem] text-base-content/40">{span}</span>
        {/if}
      </div>
    </div>

    {#if spark.length > 1 && spark.some((v) => v > 0)}
      <!-- `h-7 w-20`, matching the week card exactly: a fixed glyph, so a shape means
           the same thing in both tabs and can be compared down the column. -->
      <div class="hidden shrink-0 sm:block">
        <Sparkline
          values={spark}
          labels={sparkLabels}
          format={(v) => `${v} asked`}
          class="h-7 w-20 text-base-content/40"
        />
      </div>
    {/if}

    <svg
      class="h-3.5 w-3.5 shrink-0 text-base-content/30 transition-transform {open
        ? 'rotate-180'
        : ''}"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      stroke-width="2.5"
      aria-hidden="true"
    ><path d="M6 9l6 6 6-6" stroke-linecap="round" stroke-linejoin="round" /></svg>
  </button>

  {#if open}
    <!-- `<TopicQuestions>` brings its own top border and tint, so the panel is handed
         straight through rather than wrapped in a second one. -->
    {@render children?.()}
  {/if}
</article>
